import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { HYPHEN_MINUS } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { formatNumber, formatValueWithUnitDenominator } from '@dealstack/shared/lib/format/number';
import {
	InfoDiv,
	TotalDetailsDiv,
	UniqueDiv,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { Text, TextInput } from '@mantine/core';
import { createSortableTextHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader';

type UnitColumnBaseProps = { securityUnitDenominator: number | null };

type UnitColumnProps<T extends UnitColumnBaseProps> = ExtendedColumnDef<T> & {
	showFooter?: boolean;
	footerValue?: number;
	text?: string;
	filterText?: string;
	headerText?: string;
};

export const createUnitsColumn = <T extends UnitColumnBaseProps>({
	showFooter,
	footerValue,
	text,
	headerText,
	enableSorting = true,
	...columnDef
}: UnitColumnProps<T>): ExtendedColumnDef<T> => {
	return {
		header: headerText
			? createSortableTextHeader(
					headerText,
					enableSorting,
					{
						style: {
							width: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
						},
					},
					'left'
				)
			: columnDef.header,
		columnLabel: columnDef.columnLabel ?? headerText ?? '',
		enableSorting,
		cell: ({ cell, row }) => {
			let cellValue = cell.getValue() as string | number | null;
			if (cellValue == null) {
				cellValue = HYPHEN_MINUS;
			} else {
				const parsedValue = typeof cellValue === 'string' ? Number(cellValue) : cellValue;
				cellValue = formatValueWithUnitDenominator(
					parsedValue,
					row.original.securityUnitDenominator ?? 1
				);
			}
			return (
				<div
					style={{
						width: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
					}}
				>
					<Text size="B6Medium">{cellValue}</Text>
				</div>
			);
		},
		footer: showFooter
			? () => {
					let formattedFooterValue = '';
					if (footerValue != null) {
						formattedFooterValue = formatNumber({
							value: footerValue,
						});
					} else {
						formattedFooterValue = HYPHEN_MINUS;
					}

					return (
						<TotalDetailsDiv>
							<UniqueDiv>{text}</UniqueDiv>
							<InfoDiv>{formattedFooterValue}</InfoDiv>
						</TotalDetailsDiv>
					);
				}
			: undefined,
		filterValueType: 'number',
		filter: columnDef.showFilter
			? ({ column }) => (
					<TextInput
						value={column.getFilterValue() as string}
						onChange={(e) => column.setFilterValue(e.currentTarget.value)}
						placeholder={columnDef.filterText}
					/>
				)
			: undefined,
		...columnDef,
	};
};
